<template>
  <div class="adgroup-main-settings campaign-step">
    <div class="step-header">
      <div class="step-icon"><img src="/images/settings.svg" alt="main" /></div>
      <h2>{{ $t('messages.main_settings') }}</h2>
    </div>

    <div class="settings-content">
      <form @submit.prevent="submit">
        <div v-if="errors.length" class="form-errors">
          <ul>
            <li v-for="(e, i) in errors" :key="i" style="color:red">{{ e }}</li>
          </ul>
        </div>

        <ad-group-name :value="form.name" @update="val => form.name = val" />

        <ad-group-types :creativeType="form.creative_type" :environment="form.environment" :adFormat="form.ad_format"
          :creativeTypeOptions="creativeTypeOptions" :environments="environments" :adFormats="adFormats"
          :availableCreativeTypes="availableCreativeTypes" :availableEnvironments="availableEnvironments"
          :availableAdFormats="availableAdFormats" :availableKpis="availableKpis" :paymentModel="form.kpi"
          :priceDisplay="priceDisplay" :priceValue="form.price_value" :totalBudget="form.total_budget"
          :plannedVolume="form.planned_volume" :readonly="readonly" @update-types="onTypesUpdate" />

        <ad-group-status :value="form.status" @update="val => form.status = val" />

        <ad-group-dates :startDate="form.start_date" :endDate="form.end_date"
          :campaignStart="campaign.start_date.split('T')[0]" :campaignEnd="campaign.end_date.split('T')[0]"
          @update-dates="onDatesUpdate" />

      </form>
    </div>
  </div>
</template>

<script>
import AdGroupName from './MainSettings/AdGroupName.vue';
import AdGroupStatus from './MainSettings/AdGroupStatus.vue';
import AdGroupTypes from './MainSettings/AdGroupTypes.vue';
import AdGroupDates from './MainSettings/AdGroupDates.vue';

export default {
  name: 'AdGroupMainSettings',
  components: { AdGroupName, AdGroupStatus, AdGroupTypes, AdGroupDates },
  emits: ['update', 'created'],
  props: {
    campaign: { default: null },
    prices: { type: Array, default: () => [] },
    initialForm: { type: Object, default: null },
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      form: {
        campaign_id: this.campaign?.id || '',
        creative_type: '',
        environment: '',
        ad_format: '',
        kpi: '',
        price_id: null,
        price_value: null,
        name: '',
        status: 'active',
        total_budget: null,
        planned_volume: null,
        start_date: null,
        end_date: null
      },
      errors: [],
      submitting: false,
      backUrl: '/ad-groups',
      // creativeTypes not used — removed for clarity
      environments: [
        { value: 'web', label: 'Web' },
        { value: 'app', label: 'App' }
      ],
      adFormats: [],
      kpis: [],
      priceDisplay: '—',
      pricesList: []
    };
  },
  computed: {
    creativeTypeOptions() {
      return [
        { value: 'Programmatic', label: 'Programmatic', icon: '/images/programmatic.png' },
        { value: 'YouTube', label: 'YouTube', icon: '/images/youtube_logo.png' },
        { value: 'Meta', label: 'Meta', icon: '/images/meta_logo.png' },
        { value: 'Rich Media', label: 'Rich Media', icon: '/images/rich-media.png' },
      ];
    }
    ,
    availableCreativeTypes() {
      return this.creativeTypeOptions.map(o => o.value).filter(v => this.isAvailableFor('creative_type', v));
    },
    availableEnvironments() {
      return this.environments.map(e => e.value).filter(v => this.isAvailableFor('environment', v));
    },
    availableAdFormats() {
      return this.adFormats.filter(f => this.isAvailableFor('ad_format', f));
    },
    availableKpis() {
      return this.kpis.filter(k => this.isAvailableFor('kpi', k));
    },
  },
  mounted() {
    // If parent provided initial form (edit mode), populate local form from it
    if (this.initialForm && typeof this.initialForm === 'object') {
      this.form = { ...this.form, ...this.initialForm };
    }
    if (this.form.campaign_id) {
      const c = this.campaign;
      if (c && !this.form.name) this.form.name = `adgroup_${c.campaign}`;
      // initialize adgroup dates to campaign bounds when available
      if (c) {
        if (!this.form.start_date && c.start_date) this.form.start_date = c.start_date.split('T')[0];
        if (!this.form.end_date && c.end_date) this.form.end_date = c.end_date.split('T')[0];
        if (this.form.start_date && this.form.end_date && this.form.start_date > this.form.end_date) {
          this.form.end_date = this.form.start_date;
        }
      }
    }
    // initialize prices from server-side prop
    this.pricesList = this.prices || [];
    const af = new Set();
    const ks = new Set();
    this.pricesList.forEach(p => {
      if (p.ad_format) af.add(p.ad_format);
      if (p.kpi) ks.add(p.kpi);
    });
    const standardKpis = ['CPM', 'CPC', 'CPV', 'CPE'];
    standardKpis.forEach(k => ks.add(k));
    this.adFormats = Array.from(af);
    this.kpis = Array.from(ks);
  },
  watch: {
    'form.campaign_id'(val) {
      const c = this.campaign;
      if (c && (!this.form.name || this.form.name.trim() === '')) {
        this.form.name = `adgroup_${c.campaign}`;
      }
      // adjust adgroup dates to fit campaign bounds when campaign changes
      if (c) {
        const campStart = c.start_date ? c.start_date.split('T')[0] : null;
        const campEnd = c.end_date ? c.end_date.split('T')[0] : null;
        if (campStart) {
          if (!this.form.start_date || this.form.start_date < campStart) this.form.start_date = campStart;
        }
        if (campEnd) {
          if (!this.form.end_date || this.form.end_date > campEnd) this.form.end_date = campEnd;
        }
        if (this.form.start_date && this.form.end_date && this.form.start_date > this.form.end_date) {
          this.form.end_date = this.form.start_date;
        }
      }
    },
    'form.creative_type'() { this.onSelectionChange(); },
    'form.environment'() { this.onSelectionChange(); },
    'form.ad_format'() { this.onSelectionChange(); },
    'form.kpi'() { this.onSelectionChange(); },
    form: {
      handler() { this.emitUpdate(); },
      deep: true
    }
  },
  methods: {
    // prices are provided via props; no client-side /prices/list call required
    normalizeEnvs(s) {
      return String(s || '').split(';').map(x => x.trim()).filter(Boolean);
    },

    async onSelectionChange() {
      // only attempt match when all four selection fields are set
      if (!this.form.kpi || !this.form.creative_type || !this.form.environment || !this.form.ad_format) {
        this.priceDisplay = '—';
        this.form.price_id = null;
        this.form.price_value = null;
        return;
      }
      // match on client-side using provided pricesList
      const reqEnvs = this.normalizeEnvs(this.form.environment);
      const found = (this.pricesList || []).find(p => {
        if ((p.kpi ?? null) != this.form.kpi) return false;
        if ((p.creative_type ?? null) != this.form.creative_type) return false;
        if ((p.ad_format ?? null) != this.form.ad_format) return false;
        // price.environment may contain multiple values (e.g. "app;web")
        const priceEnvs = this.normalizeEnvs(p.environment);
        // Require AND: price must contain all selected environments
        for (const re of reqEnvs) {
          if (!priceEnvs.includes(re)) return false;
        }
        return true;
      });
      if (found) {
        const value = found.price_altel ?? found.price_client ?? found.price_cost ?? null;
        this.form.price_id = found.id;
        this.form.price_value = value;
        this.priceDisplay = (value !== null) ? `${value} ${found.currency ?? 'KZT'}` : '—';
      } else {
        this.form.price_id = null;
        this.form.price_value = null;
        this.priceDisplay = '—';
      }
    },
    // emit current form up to parent whenever it changes
    emitUpdate() {
      this.$emit('update', { ...this.form });
    },
    onTypesUpdate(payload) {
      this.form = { ...this.form, ...payload };
      this.onSelectionChange();
    },
    onDatesUpdate(payload) {
      this.form.start_date = payload.start_date || null;
      this.form.end_date = payload.end_date || null;
    },
    existsPriceWith(criteria) {
      if (!this.pricesList || this.pricesList.length === 0) return false;
      return (this.pricesList || []).some(p => {
        const keys = ['creative_type', 'ad_format', 'kpi', 'environment'];
        for (const key of keys) {
          if (!criteria[key]) continue;
          if (key === 'environment') {
            const requiredEnvs = this.normalizeEnvs(criteria.environment);
            const priceEnvs = this.normalizeEnvs(p.environment);
            for (const re of requiredEnvs) {
              if (!priceEnvs.includes(re)) return false;
            }
            continue;
          }
          if ((p[key] ?? null) != criteria[key]) return false;
        }
        return true;
      });
    },
    isAvailableFor(field, value) {
      const keys = ['creative_type', 'environment', 'ad_format', 'kpi'];
      const criteria = {};
      keys.forEach(k => {
        if (k === field) criteria[k] = value;
        else if (this.form[k]) criteria[k] = this.form[k];
      });
      // If no prices defined on server, allow selection (don't block)
      if (!this.pricesList || this.pricesList.length === 0) return true;
      return this.existsPriceWith(criteria);
    }
  }
};
</script>

<style scoped>
.field-group {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-errors {
  margin-bottom: 12px;
}

.form-group {
  margin-bottom: 12px;
}

.form-input,
.form-select {
  padding: 8px;
}

.creative-type-list {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.creative-type-item {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.creative-icon {
  width: 20px;
  height: 20px;
}
</style>
